import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import t from 'react-translate';
import {
  askNovoAiRequest,
  changeDrawerState,
  closeNovoAIDialog,
  getRecentQuestions,
  openNovoAIDialog,
} from 'redux/actions/help-options';
import { getCurrentUser } from 'redux/selectors/users';
import NvIcon from 'shared/components/nv-icon';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { getCurrentCourse } from 'redux/selectors/course';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import SendMessageModal from 'shared/components/nv-community-completion/send-message-modal';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import {
  askNovoAiContainer,
  novoAiCollapsedWrapper,
  novoAiExpandedWrapper,
} from './styles';
import InitialDrawerState from './components/initial-drawer-state';
import LoadingDrawerState from './components/loading-drawer-state';
import ResponseDrawerState from './components/response-drawer-state';
import { AskNovoAiDrawerState, AskNovoAiState } from './helpers';
import ErrorDrawerState from './components/error-drawer-state';

const AskNovoAI: React.FC = () => {
  const [formFields, setFormFields] = React.useState<string>('');
  const [showSendMessage, setSendMessage] = React.useState<boolean>(false);
  const { lecturePageId, catalogId } = useLecturePageParams();
  const dispatch = useAppDispatch();
  const currentUser = useSelector(getCurrentUser);
  const course = useSelector(getCurrentCourse);
  const { hasAiComponentGeneration } = useSelector(getCurrentInstitution);

  const askNovoAi: AskNovoAiState = useSelector(
    (state) => state.app.askNovoAi,
  );
  const { helpOptions } = askNovoAi;
  const shouldShowNovoAi = hasAiComponentGeneration && (helpOptions?.hasAiLearningAssistantEnabled || helpOptions?.hasAiEmbeddingInProgress) && !course.inArchiveMode;
  const isLecturePage = !!lecturePageId;

  const onSubmit = () => {
    if (!formFields) return;
    dispatch(askNovoAiRequest({ catalogId, questionText: formFields }));
  };

  useEffect(() => {
    // If it's open request the recent questions
    if (askNovoAi.isOpen && askNovoAi.drawerState === AskNovoAiDrawerState.INITIAL) {
      dispatch(getRecentQuestions(catalogId));
    }
  }, [askNovoAi.drawerState, askNovoAi.isOpen, catalogId, dispatch]);

  const resetState = () => {
    setFormFields('');
    dispatch(changeDrawerState(AskNovoAiDrawerState.INITIAL));
  };

  if (!shouldShowNovoAi) return null;

  if (!askNovoAi.isOpen) {
    // Collapsed state: small button in bottom right
    return (
      <div css={askNovoAiContainer(isLecturePage, false)}>
        <button
          css={novoAiCollapsedWrapper}
          onClick={() => dispatch(openNovoAIDialog())}
          type='button'
          aria-label={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.TITLE()}
        >
          <div className='novoai-collapsed-icon'>
            <NvIcon icon='gen-ai' size='medium' className='icon-gradient' />
            <span className='novoai-collapsed-label'>
              {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.TITLE()}
            </span>
          </div>
          <NvIcon icon='arrow-down' size='xs-small' className='icon-gradient' />
        </button>
      </div>
    );
  }

  // Expanded state: drawer UI
  return (
    <div css={askNovoAiContainer(isLecturePage, true)}>
      <div css={novoAiExpandedWrapper}>
        <div className='novoai-drawer-header'>
          <div className='novoai-header-left'>
            <NvIcon icon='gen-ai' size='small' className='icon-gradient' />
            <span className='novoai-title'>
              {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.TITLE()}
            </span>
          </div>
          <button
            className='novoai-drawer-close'
            aria-label='Close'
            onClick={() => dispatch(closeNovoAIDialog())}
            type='button'
          >
            <NvIcon icon='arrow-up' size='xs-small' className='icon-gradient' />
          </button>
        </div>
        <div className='novoai-drawer-content'>
          {askNovoAi.drawerState === AskNovoAiDrawerState.INITIAL && (
            <InitialDrawerState
              firstName={currentUser.firstName}
              formFields={formFields}
              setFormFields={setFormFields}
              onSubmit={onSubmit}
            />
          )}
          {askNovoAi.drawerState === AskNovoAiDrawerState.LOADING && (
            <LoadingDrawerState
              question={formFields}
              onCancel={() => dispatch(changeDrawerState(AskNovoAiDrawerState.SUCCESS))}
            />
          )}
          {askNovoAi.drawerState === AskNovoAiDrawerState.SUCCESS && (
            <ResponseDrawerState
              question={formFields || askNovoAi?.response?.questionText}
              onClick={resetState}
            />
          )}
          {askNovoAi.drawerState === AskNovoAiDrawerState.ERROR && (
            <ErrorDrawerState
              question={formFields}
              teachingTeam={helpOptions?.courseAdminUsers}
              onClick={resetState}
              setSendMessage={setSendMessage}
            />
          )}
        </div>
      </div>
      <NvModal
        type={ModalType.DYNAMIC}
        header={t.PROFILE.SEND_A_MESSAGE()}
        show={showSendMessage}
        onClose={() => setSendMessage(false)}
        doubleModal
        fullHeight={false}
        body={(
          <SendMessageModal
            subject={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.ASK_NOVO_AI_DRAWER.NEED_HELP_MESSAGE()}
            body={formFields}
            recipient={helpOptions?.courseAdminUsers
              ?.filter((item) => item.isQnaFacilitator)
              ?.map((user) => ({
                id: user.user.id,
                firstName: user.user.firstName,
                lastName: user.user.lastName,
                email: user.user.email,
                avatarUrl: '',
              })) || []}
            closeModal={() => setSendMessage(false)}
            skipDirtyCheck
          />
        )}
      />
    </div>
  );
};

export default AskNovoAI;
